import { getDeviceId, getAdminToken, addPurchase } from './access';
import { getCar } from '../cars/registry';

/**
 * Entitlement grant — the server's signed word on whether this device runs a car
 * FULL or as a DEMO. The audio files of a car are only served with the grant's
 * token (?g=…), so the client's localStorage is never the source of truth.
 */
export type GrantMode = 'full' | 'demo' | 'none';
export interface Grant {
  mode: GrantMode;
  token: string;
  exp: number;
  /** DEMO: per-token key — the audio arrives AES-CTR encrypted and is decrypted in memory only */
  key?: string;
  /** DEMO: seconds of engine the SERVER says are left today (its ledger outlives a wiped browser) */
  leftS?: number;
}

const KEY = 'roardrive:grants:v1';

function readAll(): Record<string, Grant> {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? '{}') as Record<string, Grant>;
  } catch {
    return {};
  }
}
function writeAll(all: Record<string, Grant>): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    /* ignore */
  }
}

/** Ask the server (cached until shortly before expiry; `force` after a purchase). */
export async function requestGrant(carId: string, force = false): Promise<Grant | null> {
  if (getCar(carId)?.free) return { mode: 'full', token: '', exp: Number.MAX_SAFE_INTEGER };
  const all = readAll();
  const cached = all[carId];
  // A DEMO PASS IS SPENT BY THE DRIVE THAT USED IT. The server hands each demo file out once per
  // token ('demo: already delivered'), so a saved demo token reused by a second start (a reload, the
  // Tesla restarting its tab, back to the bay and in again within its four minutes) got a 403 for
  // every recording and the engine sat on LOADING for ever (Marc in the 458, 2026-10-25). A demo start
  // always asks for a new one; the seconds are the server's ledger, so a new token buys no extra time.
  if (!force && cached && cached.mode !== 'demo' && cached.exp - Date.now() > 60_000) return cached;
  try {
    const r = await fetch('/api/grant', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ deviceId: getDeviceId(), carId, adminToken: getAdminToken() ?? undefined }),
    });
    if (!r.ok) return cached ?? null;
    const j = (await r.json()) as { mode: GrantMode; token?: string; exp?: number; k?: string; leftS?: number };
    const g: Grant = { mode: j.mode, token: j.token ?? '', exp: j.exp ?? Date.now() + 60_000, key: j.k, leftS: j.leftS };
    if (g.mode === 'full') addPurchase(carId); // keep the Home badge honest
    all[carId] = g;
    writeAll(all);
    return g;
  } catch {
    // offline in the car: last known grant keeps the cached audio playable
    return cached ?? null;
  }
}

/** Forget the cached grant for a car (after a purchase the next request must ask again). */
export function invalidateGrant(carId: string): void {
  const all = readAll();
  delete all[carId];
  writeAll(all);
}

/** Tell the server how much demo engine this device just burned; it answers with what is left today. */
export async function spendDemo(carId: string, seconds: number): Promise<number | null> {
  try {
    const r = await fetch('/api/demo/spend', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ deviceId: getDeviceId(), carId, seconds: Math.round(seconds) }),
    });
    if (!r.ok) return null;
    const j = (await r.json()) as { leftS?: number };
    return typeof j.leftS === 'number' ? j.leftS : null;
  } catch {
    return null;   // offline in the car: the local clock keeps the demo honest
  }
}
